import React, { useEffect, useState } from "react";
import Loader from "react-dots-loader";
import "react-dots-loader/index.css";

import Utils from "../../../Components/utils";

import "./LabelAvailability.scss";

// Criptografia AES-256-CBC para parâmetros do PDF (evita bloqueio pelo WAF/Citrix em prod)
const PDF_SALT = "$2b$10$M1RHYatP0jz1q859zZS26e";

const encryptPdfParams = async (params) => {
  const encoder = new TextEncoder();
  const keyData = await crypto.subtle.digest("SHA-256", encoder.encode(PDF_SALT));
  const key = await crypto.subtle.importKey("raw", keyData, { name: "AES-CBC" }, false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const plaintext = encoder.encode(JSON.stringify(params));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-CBC", iv }, key, plaintext);
  const ivHex = Array.from(iv).map(b => b.toString(16).padStart(2, "0")).join("");
  const encryptedB64 = btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
  return `${ivHex}:${encryptedB64}`;
};

const getAuthToken = () => {
  const ldata = localStorage.getItem("logedInUser");
  return ldata ? JSON.parse(ldata).token : null;
};

const hubGet = async (url) => {
  const token = getAuthToken();
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 403) {
    const error = new Error("Você não tem acesso a este produto/planta.");
    error.forbidden = true;
    throw error;
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const IDIOMAS = [
  { label: "Português (Brasil)", culture: "pt-BR", flag: "🇧🇷" },
  { label: "English (United States)", culture: "en-US", flag: "🇺🇸" },
  { label: "Español (España)", culture: "es-ES", flag: "🇪🇸" },
  { label: "Español (Chile)", culture: "es-CL", flag: "🇨🇱" },
];

const PAISES_CHILENO = new Set([18]);
const PAISES_ESPANHOL = new Set([22, 171, 158, 217, 41, 14, 30, 55, 209, 45, 111, 177, 230, 286, 71]);

const LINE_CHILENO = new Set(["CHILE"]);
const LINE_ESPANHOL = new Set([
  "MEXICO", "SPAIN", "COLOMBIA", "EQUADOR", "ECUADOR", "PARAGUAY",
  "PERU", "ARGENTINA", "URUGUAY", "BOLIVIA", "SURINAME", "CURACAO",
  "CURAÇAO", "GUATEMALA", "REPUBLICA DOMINICANA", "EL SALVADOR", "ARUBA",
]);

// A coluna "Plant of Production" (empresa) só deve aparecer quando o produto exigir,
// controlado por flg_mostra_empresa (MostraEmpresa) na view crm_hub.vw_cad_produto_empresa_etiqueta.
// 1 = exibe e empresa é obrigatória no download; 0 = não exibe e envia a empresa padrão (MTZ).
const shouldShowPlant = (product) => {
  const flag = product.MostraEmpresa ?? product.mostraEmpresa ?? 0;
  return Number(flag) === 1;
};


const getSuggestedCultures = (destinationCountryCode, line) => {
  if (destinationCountryCode) {
    if (PAISES_CHILENO.has(destinationCountryCode)) return ["es-CL", "en-US"];
    if (PAISES_ESPANHOL.has(destinationCountryCode)) return ["es-ES", "en-US"];
    return ["en-US"];
  }
  if (line) {
    const lineUpper = line.toUpperCase().trim();
    if (LINE_CHILENO.has(lineUpper)) return ["es-CL", "en-US"];
    if (LINE_ESPANHOL.has(lineUpper)) return ["es-ES", "en-US"];
    return ["en-US"];
  }
  return null;
};

const LabelAvailability = () => {
  const [filterProductCode, setFilterProductCode] = useState("");
  const [filterContractCode, setFilterContractCode] = useState("");

  const [fullData, setFullData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState({});
  const [empresaSearch, setEmpresaSearch] = useState({});
  const [openEmpresaMenu, setOpenEmpresaMenu] = useState(null);
  const [empresaMenuPos, setEmpresaMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const [openIdiomMenu, setOpenIdiomMenu] = useState(null);
  const [idiomMenuPos, setIdiomMenuPos] = useState({ top: 0, left: 0 });
  const [suggestedEmpresa, setSuggestedEmpresa] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  // Guarda o objeto completo de cada produto selecionado, acumulando entre páginas.
  // Assim o download em lote considera seleções de todas as páginas, não só a atual.
  const [selectedProducts, setSelectedProducts] = useState({});
  const [bulkCulture, setBulkCulture] = useState("pt-BR");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [downloadingProduct, setDownloadingProduct] = useState(null);
  const [productContractMap, setProductContractMap] = useState({});
  const [validEmpresasByProduct, setValidEmpresasByProduct] = useState({});

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Guarda os productCodes retornados por my-contracts para usar na paginação
  const [lastProductCodes, setLastProductCodes] = useState([]);
  const [lastContractCode, setLastContractCode] = useState(null);

  // Busca os contratos do usuário filtrados pelo backend (1 request)
  const fetchMyContracts = async (productCodeFilter, contractFilter) => {
    const params = new URLSearchParams();
    if (productCodeFilter) params.set("productCode", productCodeFilter);
    if (contractFilter) params.set("contractCode", contractFilter);

    try {
      const response = await hubGet(
        `${process.env.REACT_APP_URL}/api/hub/v1/product/my-contracts${params.toString() ? `?${params}` : ""}`
      );
      return response?.data ?? [];
    } catch (error) {
      console.error("my-contracts indisponível:", error.message);
      return null;
    }
  };

  // Busca produtos paginados no backend (1 request)
  // Obtém o(s) erpConta da(s) conta(s) selecionada(s) no header
  const getSelectedErpContas = () => {
    try {
      const accounts = JSON.parse(localStorage.getItem("accounts") || "[]");
      const erps = accounts.map((a) => a.erpConta).filter(Boolean);
      return erps.length > 0 ? erps.join(",") : null;
    } catch { return null; }
  };

  const fetchProductsPaginated = async (productCodes, contractCode, page, size) => {
    const params = new URLSearchParams();
    if (productCodes && productCodes.length > 0) params.set("productCodes", productCodes.join(","));
    if (contractCode) params.set("contractCode", contractCode);
    const erpContas = getSelectedErpContas();
    if (erpContas) params.set("erpConta", erpContas);
    params.set("page", String(page));
    params.set("pageSize", String(size));

    try {
      const response = await hubGet(
        `${process.env.REACT_APP_URL}/api/hub/v1/product/list-paginated?${params.toString()}`
      );
      return response?.data ?? null;
    } catch (error) {
      console.error("list-paginated indisponível:", error.message);
      return null;
    }
  };

  const handleSearch = async (page = 1) => {
    if (!filterProductCode && !filterContractCode) {
      Utils.Alert({ title: "Attention", text: "Fill in at least one filter before searching." });
      return;
    }

    setLoading(true);
    try {
      // Se só productCode preenchido: vai direto para list-paginated (não depende de my-contracts)
      if (filterProductCode && !filterContractCode) {
        const paginatedResult = await fetchProductsPaginated(
          [Number(filterProductCode)], null, page, pageSize
        );

        if (!paginatedResult || (paginatedResult.Items ?? paginatedResult.items ?? []).length === 0) {
          Utils.Alert({
            title: "No Products Found",
            text: "No products found, or you are not authorized to access them.",
          });
          setFullData([]);
          setProductContractMap({});
          setTotalCount(0);
          setTotalPages(0);
          return;
        }

        const items = paginatedResult.Items ?? paginatedResult.items ?? [];
        setFullData(items);
        setProductContractMap({});
        setTotalCount(paginatedResult.TotalCount ?? paginatedResult.totalCount ?? 0);
        setTotalPages(paginatedResult.TotalPages ?? paginatedResult.totalPages ?? 0);
        setCurrentPage(paginatedResult.Page ?? paginatedResult.page ?? 1);
        setLastProductCodes([Number(filterProductCode)]);
        setLastContractCode(null);

        // Buscar plantas válidas em batch (1 request para todos os produtos da página)
        const validEmpresas = {};
        try {
          const productIdsParam = items.map((p) => p.ProductCode ?? p.productCode).join(",");
          const batchResponse = await hubGet(
            `${process.env.REACT_APP_URL}/api/hub/v1/product/valid-plants-batch?productIds=${productIdsParam}`
          );
          const batchData = batchResponse?.data ?? {};
          Object.entries(batchData).forEach(([code, plants]) => {
            if (plants && plants.length > 0) {
              validEmpresas[Number(code)] = plants;
            }
          });
        } catch { /* fallback: sem restrição de plantas */ }

        setSelectedEmpresa({});
        setEmpresaSearch({});
        setSuggestedEmpresa({});
        setValidEmpresasByProduct(validEmpresas);
        return;
      }

      // Se contractCode preenchido: usa my-contracts para descobrir os productCodes do contrato
      const contractRows = await fetchMyContracts(filterProductCode || null, filterContractCode || null);

      if (contractRows === null) {
        Utils.Alert({
          title: "Indisponível no momento",
          text: "A busca por contrato está temporariamente indisponível. Tente buscar apenas pelo código do produto.",
        });
        setFullData([]);
        setTotalCount(0);
        setTotalPages(0);
        return;
      }

      // Extrair productCodes dos contratos retornados
      const productCodes = [...new Set(
        contractRows.map((row) => Number(row.ProductCode ?? row.productCode)).filter(Boolean)
      )];

      // Construir mapa produto→contrato
      const contractMap = {};
      contractRows.forEach((row) => {
        const code = String(row.ProductCode ?? row.productCode);
        const contract = String(row.ContractCode ?? row.contractCode ?? "").trim();
        if (code && contract) contractMap[code] = contract;
      });

      if (productCodes.length === 0) {
        Utils.Alert({
          title: "No Products Found",
          text: "No products found for this contract. Check the contract code or verify that you have authorization to access this contract.",
        });
        setFullData([]);
        setProductContractMap({});
        setTotalCount(0);
        setTotalPages(0);
        return;
      }

      // Buscar produtos paginados
      const paginatedResult = await fetchProductsPaginated(
        productCodes,
        filterContractCode || null,
        page,
        pageSize
      );

      if (!paginatedResult || (paginatedResult.Items ?? paginatedResult.items ?? []).length === 0) {
        Utils.Alert({
          title: "No Products Found",
          text: "No products found for the given filters.",
        });
        setFullData([]);
        setProductContractMap({});
        setTotalCount(0);
        setTotalPages(0);
        return;
      }

      const items = paginatedResult.Items ?? paginatedResult.items ?? [];
      setFullData(items);
      setProductContractMap(contractMap);
      setTotalCount(paginatedResult.TotalCount ?? paginatedResult.totalCount ?? 0);
      setTotalPages(paginatedResult.TotalPages ?? paginatedResult.totalPages ?? 0);
      setCurrentPage(paginatedResult.Page ?? paginatedResult.page ?? 1);
      setLastProductCodes(productCodes);
      setLastContractCode(filterContractCode || null);

      // 3. Buscar apenas as plantas válidas em batch (1 request).
      // Sem prefill/sugestão: o campo começa vazio e o usuário seleciona manualmente.
      const validEmpresas = {};
      try {
        const productIdsParam = items.map((p) => p.ProductCode ?? p.productCode).join(",");
        const batchResponse = await hubGet(
          `${process.env.REACT_APP_URL}/api/hub/v1/product/valid-plants-batch?productIds=${productIdsParam}`
        );
        const batchData = batchResponse?.data ?? {};
        Object.entries(batchData).forEach(([code, plants]) => {
          if (plants && plants.length > 0) {
            validEmpresas[Number(code)] = plants;
          }
        });
      } catch { /* fallback: sem restrição */ }

      setSelectedEmpresa({});
      setEmpresaSearch({});
      setSuggestedEmpresa({});
      setValidEmpresasByProduct(validEmpresas);
    } catch (error) {
      console.error("Error loading products:", error);
      Utils.Alert({ title: "Erro", text: "Falha ao carregar produtos. Tente novamente." });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterProductCode("");
    setFilterContractCode("");
    setFullData([]);
    setSelectedEmpresa({});
    setEmpresaSearch({});
    setSuggestedEmpresa({});
    setSelectedRows({});
    setSelectedProducts({});
    setProductContractMap({});
    setValidEmpresasByProduct({});
    setCurrentPage(1);
    setTotalCount(0);
    setTotalPages(0);
    setLastProductCodes([]);
    setLastContractCode(null);
  };

  // Carrega plantas válidas (batch) e sugeridas por contrato para uma lista de itens,
  // MESCLANDO no estado existente (não substitui) — necessário para manter os dados
  // dos produtos selecionados em páginas anteriores ao navegar.
  const loadPlantsForItems = async (items) => {
    const validEmpresas = {};

    try {
      const productIdsParam = items.map((p) => p.ProductCode ?? p.productCode).join(",");
      const batchResponse = await hubGet(
        `${process.env.REACT_APP_URL}/api/hub/v1/product/valid-plants-batch?productIds=${productIdsParam}`
      );
      const batchData = batchResponse?.data ?? {};
      Object.entries(batchData).forEach(([code, plants]) => {
        if (plants && plants.length > 0) {
          validEmpresas[Number(code)] = plants;
        }
      });
    } catch { /* fallback: sem restrição */ }

    // Sem prefill de planta sugerida: apenas carrega as plantas válidas para seleção.
    setValidEmpresasByProduct((prev) => ({ ...prev, ...validEmpresas }));
  };

  // Navegar para outra página (usa os mesmos productCodes da busca original)
  const goToPage = async (page) => {
    if (page < 1 || page > totalPages) return;
    setLoading(true);
    try {
      const paginatedResult = await fetchProductsPaginated(
        lastProductCodes,
        lastContractCode,
        page,
        pageSize
      );
      if (paginatedResult) {
        const items = paginatedResult.Items ?? paginatedResult.items ?? [];
        setFullData(items);
        setCurrentPage(paginatedResult.Page ?? paginatedResult.page ?? page);
        setTotalCount(paginatedResult.TotalCount ?? paginatedResult.totalCount ?? 0);
        setTotalPages(paginatedResult.TotalPages ?? paginatedResult.totalPages ?? 0);
        // Carrega as plantas válidas da nova página (sem prefill de sugestão)
        await loadPlantsForItems(items);
      }
    } catch (error) {
      console.error("Erro ao mudar página:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mantém selectedProducts em sincronia com selectedRows: quando uma linha é marcada,
  // guarda o objeto completo do produto (procurando na página atual); quando desmarca, remove.
  const setRowSelection = (code, product, checked) => {
    setSelectedRows((prev) => ({ ...prev, [code]: checked }));
    setSelectedProducts((prev) => {
      const next = { ...prev };
      if (checked) {
        next[code] = product ?? prev[code];
      } else {
        delete next[code];
      }
      return next;
    });
  };

  const toggleRowFull = (product) => {
    const code = product.ProductCode ?? product.productCode;
    setRowSelection(code, product, !selectedRows[code]);
  };

  const toggleAllRows = () => {
    const allSelectedOnPage = fullData.every((p) => selectedRows[p.ProductCode ?? p.productCode]);
    fullData.forEach((p) => {
      const code = p.ProductCode ?? p.productCode;
      setRowSelection(code, p, !allSelectedOnPage);
    });
  };

  // Conta e baixa considerando TODAS as páginas (mapa acumulado), não só a atual.
  const selectedCount = Object.keys(selectedProducts).length;
  const allSelected = fullData.length > 0 && fullData.every((p) => selectedRows[p.ProductCode ?? p.productCode]);

  // Resolve qual empresa enviar.
  // - flg_mostra_empresa = 1: o usuário escolheu no dropdown (selectedEmpresa).
  // - flg_mostra_empresa = 0: a coluna fica oculta e enviamos vazio; o default MTZ é
  //   aplicado na API apenas para o PQ Online, sem afetar a validação de acesso.
  const resolveEmpresaForDownload = (product) => {
    const code = product.ProductCode ?? product.productCode;
    if (shouldShowPlant(product)) {
      return selectedEmpresa[code] || "";
    }
    return "";
  };

  const handleBulkDownload = async () => {
    const toDownload = Object.values(selectedProducts);

    // Empresa só precisa de escolha manual para produtos que exibem "Plant of Production".
    // Para os demais, o sistema resolve a planta válida automaticamente.
    const missing = toDownload.filter((p) => {
      const code = p.ProductCode ?? p.productCode;
      return shouldShowPlant(p) && !selectedEmpresa[code];
    });

    if (missing.length > 0) {
      Utils.Alert({ title: "Attention", text: `${missing.length} product(s) without selected Plant of Production.` });
      return;
    }

    setBulkDownloading(true);
    const token = getAuthToken();

    let success = 0;
    let errors = 0;
    const errorMessages = [];

    for (let i = 0; i < toDownload.length; i++) {
      const product = toDownload[i];
      const code = product.ProductCode ?? product.productCode;
      // Empresa escolhida (flg=1) ou resolvida automaticamente pela 1ª planta válida (flg=0).
      const empresa = resolveEmpresaForDownload(product);

      try {
        const payload = await encryptPdfParams({ productId: code, culture: bulkCulture, empresa, user: "Sistema" });
        const url = `${process.env.REACT_APP_URL}/portal/product/pdf`;

        let response = null;
        const maxRetries = 2;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          response = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ payload }),
          });

          // Se der 504 (Gateway Timeout), tenta novamente após 2s
          if (response.status === 504 && attempt < maxRetries) {
            await new Promise((r) => setTimeout(r, 2000));
            continue;
          }
          break;
        }

        if (response.status === 403) {
          errorMessages.push(`Product ${code}: você não tem acesso a esse produto/empresa`);
          errors++;
          continue;
        }
        if (response.status === 504) {
          errorMessages.push(`Product ${code}: timeout - o servidor demorou demais para gerar o PDF`);
          errors++;
          continue;
        }
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          errorMessages.push(`Product ${code}: ${errorData?.message || `HTTP ${response.status}`}`);
          errors++;
          continue;
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = empresa ? `produto_${code}_${empresa}.pdf` : `produto_${code}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        success++;
      } catch {
        errorMessages.push(`Product ${code}: connection failure`);
        errors++;
      }
    }

    setBulkDownloading(false);
    if (errors > 0) {
      Utils.Alert({
        title: "Download completed",
        text: `${success} successfully generated. ${errors} failed.\n${errorMessages.join("\n")}`,
      });
    }
  };

  const handleEmpresaSearch = (productCode, text) => {
    setEmpresaSearch((prev) => ({ ...prev, [productCode]: text }));
    setOpenEmpresaMenu(productCode);
  };

  const handleEmpresaSelect = (productCode, sif, sigla) => {
    setSelectedEmpresa((prev) => ({ ...prev, [productCode]: sigla }));
    setEmpresaSearch((prev) => ({ ...prev, [productCode]: `${sigla} (${sif})` }));
    setSuggestedEmpresa((prev) => ({ ...prev, [productCode]: false }));
    setOpenEmpresaMenu(null);
  };

  // A API do HUB pode retornar os campos da empresa em PascalCase ou camelCase
  // dependendo do serializador da requisição de origem — normaliza os dois aqui
  // pra não espalhar `?? ` de casing pelo componente.
  const normalizeEmpresa = (emp) => ({
    sif: emp.Sif ?? emp.sif,
    sigla: emp.Sigla ?? emp.sigla,
  });

  const findEmpresaBySif = (sif) => {
    const emp = empresas.find((e) => (e.Sif ?? e.sif) === sif);
    return emp ? normalizeEmpresa(emp) : null;
  };

  const getFilteredEmpresas = (productCode) => {
    const search = (empresaSearch[productCode] || "").toLowerCase();
    const empresasList = validEmpresasByProduct[productCode] ?? empresas;
    return empresasList.filter((emp) => {
      const sif = (emp.Sif ?? emp.sif ?? "").toLowerCase();
      const sigla = (emp.Sigla ?? emp.sigla ?? "").toLowerCase();
      return sif.includes(search) || sigla.includes(search);
    });
  };

  const handleDownload = async (product, culture) => {
    const code = product.ProductCode ?? product.productCode;
    const requiresPlant = shouldShowPlant(product);
    // Empresa escolhida (flg=1) ou resolvida automaticamente pela 1ª planta válida (flg=0).
    const empresa = resolveEmpresaForDownload(product);

    if (requiresPlant && !selectedEmpresa[code]) {
      Utils.Alert({ title: "Attention", text: "Select a company before generating the PDF." });
      return;
    }

    setOpenIdiomMenu(null);
    setDownloadingProduct(code);

    try {
      const token = getAuthToken();

      const payload = await encryptPdfParams({ productId: code, culture, empresa, user: "Sistema" });
      const url = `${process.env.REACT_APP_URL}/portal/product/pdf`;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ payload }),
      });

      if (response.status === 403) {
        throw new Error("Você não tem acesso a este produto/empresa.");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Erro ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = empresa ? `produto_${code}_${empresa}.pdf` : `produto_${code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      Utils.Alert({ title: "Erro", text: error.message || "Failed to generate PDF. Please try again." });
    } finally {
      setDownloadingProduct(null);
    }
  };

  useEffect(() => {
    const loadEmpresas = async () => {
      try {
        const response = await hubGet(
          `${process.env.REACT_APP_URL}/api/hub/v1/empresa/basic`
        );
        setEmpresas(response?.data ?? []);
      } catch {
        // lista de empresas não crítica — falha silenciosa
      }
    };
    loadEmpresas();
  }, []);

  useEffect(() => {
    const closeMenu = () => {
      setOpenIdiomMenu(null);
      setOpenEmpresaMenu(null);
    };
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  return (
    <div className="labelAvailabilityContent">
      <div className="mainGrid">
        {/* Painel de filtros */}
        <aside className="filtersPanel">
          <h3 className="filtersTitle">FILTER PRODUCTS</h3>

          <div className="filterGroup">
            <label>Product Code</label>
            <input
              type="number"
              placeholder="Filter Products"
              value={filterProductCode}
              onChange={(e) => setFilterProductCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch(1)}
            />
          </div>

          <div className="filterGroup">
            <label>Contract&Load</label>
            <input
              type="text"
              placeholder="Filter by Contracts"
              value={filterContractCode}
              onChange={(e) => setFilterContractCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch(1)}
            />
          </div>

          <button className="searchBtn" onClick={() => handleSearch(1)}>
          SEARCH
          </button>

          {fullData.length > 0 && (
            <button className="clearBtn" onClick={clearFilters}>
              CLEAR
            </button>
          )}
        </aside>

        {/* Tabela de resultados */}
        <div className="resultsArea">
          {loading ? (
            <div className="loaderWrapper">
              <Loader color="#F5F5F5" />
            </div>
          ) : fullData.length === 0 ? (
            <div className="emptyState">
              <p>Use the filters on the side and click <strong>SEARCH</strong> to see the products.</p>
            </div>
          ) : (
            <>
              <div className="bulkBar">
                <span className="bulkBar__count">
                  {selectedCount} selected(s)
                </span>
                <div className="bulkBar__controls">
                  <select
                    className="bulkBar__select"
                    value={bulkCulture}
                    onChange={(e) => setBulkCulture(e.target.value)}
                  >
                    {IDIOMAS.map((i) => (
                      <option key={i.culture} value={i.culture}>{i.flag} {i.label}</option>
                    ))}
                  </select>
                  <button
                    className="bulkBar__btn"
                    onClick={handleBulkDownload}
                    disabled={selectedCount === 0 || bulkDownloading}
                  >
                    {bulkDownloading ? "Gerando..." : `Download ${selectedCount > 0 ? selectedCount : ""} PDF(s)`}
                  </button>
                </div>
              </div>
              <p className="resultsCount">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
              </p>
              <div className="tableWrapper">
                <table className="productsTable">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="rowCheckbox"
                          checked={allSelected}
                          onChange={toggleAllRows}
                        />
                      </th>
                      <th>Code</th>
                      <th>Product</th>
                      <th>Product Line</th>
                      <th>Suggested Language</th>
                      <th>Plant of Production</th>
                      <th>View Product Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fullData.map((product) => {
                      const code = product.ProductCode ?? product.productCode;
                      return (
                      <tr key={code} className={selectedRows[code] ? "rowSelected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            className="rowCheckbox"
                            checked={!!selectedRows[code]}
                            onChange={() => toggleRowFull(product)}
                          />
                        </td>
                        <td>{code}</td>
                        <td>{product.OtherLanguageProductName ?? product.otherLanguageProductName ?? product.ProductName ?? product.productName}</td>
                        <td>{product.Line ?? product.line}</td>
                        <td>
                          {(() => {
                            const countryCode = product.DestinationCountryCode ?? product.destinationCountryCode;
                            const line = product.Line ?? product.line;
                            const suggested = getSuggestedCultures(countryCode, line);
                            if (!suggested) return <span className="langBadge langBadge--neutral">—</span>;
                            return suggested.map((culture) => {
                              const idioma = IDIOMAS.find((i) => i.culture === culture);
                              return idioma ? (
                                <span key={culture} className="langBadge">
                                  {idioma.flag} {idioma.label}
                                </span>
                              ) : null;
                            });
                          })()}
                        </td>
                        <td>
                          {!shouldShowPlant(product) ? (
                            <span className="langBadge langBadge--neutral">—</span>
                          ) : (
                          <div
                            className="empresaDropdown"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {suggestedEmpresa[code] && (
                              <span className="empresaSuggestedBadge">Sugerido</span>
                            )}
                            <input
                              className={`empresaInput${suggestedEmpresa[code] ? " empresaInput--suggested" : ""}`}
                              type="text"
                              placeholder="Type to search..."
                              value={
                                openEmpresaMenu === code
                                  ? (empresaSearch[code] ?? "")
                                  : selectedEmpresa[code]
                                    ? (() => {
                                        const emp = findEmpresaBySif(selectedEmpresa[code]);
                                        return emp ? `${emp.sigla} (${emp.sif})` : selectedEmpresa[code];
                                      })()
                                    : ""
                              }
                              onChange={(e) => {
                                setSuggestedEmpresa((prev) => ({ ...prev, [code]: false }));
                                handleEmpresaSearch(code, e.target.value);
                              }}
                              onFocus={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                setEmpresaMenuPos({
                                  top: rect.bottom + window.scrollY,
                                  left: rect.left + window.scrollX,
                                  width: rect.width,
                                });
                                setEmpresaSearch((prev) => ({ ...prev, [code]: "" }));
                                setOpenEmpresaMenu(code);
                              }}
                            />
                            {openEmpresaMenu === code && getFilteredEmpresas(code).length > 0 && (
                              <ul
                                className="empresaMenu"
                                style={{
                                  position: "fixed",
                                  top: empresaMenuPos.top,
                                  left: empresaMenuPos.left,
                                  width: empresaMenuPos.width,
                                  zIndex: 9999,
                                }}
                              >
                                {getFilteredEmpresas(code).map((emp) => {
                                  const sif = emp.Sif ?? emp.sif;
                                  const sigla = emp.Sigla ?? emp.sigla;
                                  return (
                                    <li
                                      key={sif}
                                      onMouseDown={() => handleEmpresaSelect(code, sif, sigla)}
                                    >
                                      {sigla} ({sif})
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                          )}
                        </td>
                        <td>
                          <div className="idiomDropdown">
                            <button
                              className="idiomBtn"
                              disabled={downloadingProduct === code}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (downloadingProduct === code) return;
                                if (openIdiomMenu === code) {
                                  setOpenIdiomMenu(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setIdiomMenuPos({
                                    top: rect.bottom + window.scrollY,
                                    left: rect.right + window.scrollX,
                                  });
                                  setOpenIdiomMenu(code);
                                }
                              }}
                            >
                              {downloadingProduct === code ? (
                                <Loader color="#FFFFFF" size={6} />
                              ) : (
                                <>Select language... <span className="arrow">▾</span></>
                              )}
                            </button>
                            {openIdiomMenu === code && (
                              <ul
                                className="idiomMenu"
                                style={{
                                  position: "fixed",
                                  top: idiomMenuPos.top,
                                  left: idiomMenuPos.left,
                                  transform: "translateX(-100%)",
                                  zIndex: 9999,
                                }}
                              >
                                {IDIOMAS.map((idioma) => (
                                  <li
                                    key={idioma.culture}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(product, idioma.culture);
                                    }}
                                  >
                                    <span className="flag">{idioma.flag}</span>
                                    {idioma.label}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Paginação */}
              {totalPages > 1 && (
                <div className="pagination" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", margin: "16px 0" }}>
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(1)}
                    style={{ padding: "6px 10px", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}
                  >
                    &laquo;
                  </button>
                  <button
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                    style={{ padding: "6px 10px", cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}
                  >
                    &lsaquo;
                  </button>
                  <span style={{ fontWeight: "bold" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                    style={{ padding: "6px 10px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
                  >
                    &rsaquo;
                  </button>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() => goToPage(totalPages)}
                    style={{ padding: "6px 10px", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelAvailability;

